import { useState, useEffect } from 'react';
import { CurrencyInput } from '../../../components/base/CurrencyInput';
import type { SpendingAlertConfig } from '../../../hooks/useSpendingAlerts';

interface Props {
  open: boolean;
  config: SpendingAlertConfig;
  saving: boolean;
  onClose: () => void;
  onSave: (cfg: SpendingAlertConfig) => Promise<boolean>;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const SpendingAlertConfigModal = ({
  open,
  config,
  saving,
  onClose,
  onSave,
}: Props) => {
  const [draft, setDraft] = useState<SpendingAlertConfig>(config);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setDraft(config); setLocalError(null); }
  }, [open, config]);

  if (!open) return null;

  const handleSave = async () => {
    setLocalError(null);
    const cleaned: SpendingAlertConfig = {
      ...draft,
      warning_pct: Math.min(draft.warning_pct, draft.danger_pct - 1),
      custom_income: draft.custom_income && draft.custom_income > 0
        ? draft.custom_income
        : null,
    };
    const success = await onSave(cleaned);
    if (success) {
      onClose();
    } else {
      setLocalError('Erro ao salvar a configuração. Tente novamente.');
    }
  };

  const inputCls =
    'w-full h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 transition-all';

  const previewPct = draft.custom_income && draft.custom_income > 0
    ? null
    : null;
  void previewPct;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center">
              <i className="ri-alarm-warning-line text-amber-600 text-base" />
            </div>
            <div>
              <h3 className="text-sm font-700 text-gray-900">Alertas de Gasto</h3>
              <p className="text-xs text-gray-400">Configure os limites de notificação</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm font-600 text-gray-800">Ativar alertas automáticos</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Aparece no dashboard quando os limites forem atingidos
              </p>
            </div>
            <button
              onClick={() => setDraft(p => ({ ...p, enabled: !p.enabled }))}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer overflow-hidden ${
                draft.enabled ? 'bg-emerald-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  draft.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {draft.enabled && (
            <>
              {/* Custom income */}
              <div>
                <label className="block text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5">
                  Renda mensal de referência
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-500 z-10 pointer-events-none">R$</span>
                  <CurrencyInput
                    value={draft.custom_income ?? 0}
                    onChange={(v) =>
                      setDraft(p => ({
                        ...p,
                        custom_income: v > 0 ? v : null,
                      }))
                    }
                    placeholder="0,00"
                    className={`${inputCls} pl-8`}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {draft.custom_income && draft.custom_income > 0
                    ? `Referência fixada em ${formatCurrency(draft.custom_income)}`
                    : 'Usando as receitas reais lançadas no mês atual'}
                </p>
              </div>

              {/* Warning threshold */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-600 text-gray-500 uppercase tracking-wide">
                    Alerta amarelo (atenção)
                  </label>
                  <span className="text-sm font-700 text-amber-600">{draft.warning_pct}%</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={draft.danger_pct - 1}
                  value={draft.warning_pct}
                  onChange={e =>
                    setDraft(p => ({ ...p, warning_pct: Number(e.target.value) }))
                  }
                  className="w-full accent-amber-500 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                  <span>10%</span>
                  <span>{draft.danger_pct - 1}%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Banner amarelo quando gastos ultrapassarem {draft.warning_pct}% da renda
                </p>
              </div>

              {/* Danger threshold */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-600 text-gray-500 uppercase tracking-wide">
                    Alerta vermelho (crítico)
                  </label>
                  <span className="text-sm font-700 text-red-500">{draft.danger_pct}%</span>
                </div>
                <input
                  type="range"
                  min={draft.warning_pct + 1}
                  max={200}
                  value={draft.danger_pct}
                  onChange={e =>
                    setDraft(p => ({ ...p, danger_pct: Number(e.target.value) }))
                  }
                  className="w-full accent-red-500 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                  <span>{draft.warning_pct + 1}%</span>
                  <span>200%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Banner vermelho quando gastos ultrapassarem {draft.danger_pct}% da renda
                </p>
              </div>

              {/* Preview */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs font-600 text-gray-500 mb-2">Pré-visualização dos níveis</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-400 shrink-0" />
                    <span className="text-xs text-gray-600">
                      Verde: gastos abaixo de {draft.warning_pct}% da renda
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" />
                    <span className="text-xs text-gray-600">
                      Amarelo: entre {draft.warning_pct}% e {draft.danger_pct - 1}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
                    <span className="text-xs text-gray-600">
                      Vermelho: {draft.danger_pct}% ou mais
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 space-y-3">
          {/* Save error feedback */}
          {localError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl">
              <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
              <p className="text-xs text-red-600 font-500">{localError}</p>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
            >
              {saving && <i className="ri-loader-4-line animate-spin text-sm" />}
              Salvar configuração
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
